import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Download, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { capabilities } from '../data/mockData';

const Capabilities = () => {
  const handleDownload = (url, title) => {
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-transparent">
      <Helmet>
        <title>Capabilities | SALAR Technologies — Engineering-First Technical Capabilities</title>
        <meta name="description" content="SALAR's engineering-first capabilities for resilient electronics: board reliability, predictive failure analytics, semiconductor supply-chain visibility, manufacturing stability, and AI-certified lithography control. Public-safe, explainable, defense-ready." />
        <meta property="og:title" content="SALAR Capabilities — Engineering-First Technical Capabilities" />
        <meta property="og:description" content="Five capability lanes: reliability, predictive analytics, supply-chain intelligence, manufacturing stability, lithography control. All public-safe and explainable." />
        <meta property="og:url" content="https://www.salartechnologies.com/capabilities" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.salartechnologies.com/capabilities" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Hero */}
      <section
        className="text-white py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.65), rgba(10,22,40,0.9)), url('/img/circuit_chip.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="capabilities-hero"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Capabilities</h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Engineering-centered technical capabilities for resilient electronics and mission-critical systems.
            </p>
          </div>
        </div>
      </section>

      {/* Downloadable Resources Section - NEW */}
      <section className="py-12 bg-gradient-to-b from-blue-950/40 to-blue-900/20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Capabilities and Quad Chart</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Capability Statement */}
              <Card className="bg-blue-900/30 border-cyan-400/30 hover:shadow-2xl hover:shadow-cyan-500/20 transition-all duration-300 cursor-pointer" onClick={() => handleDownload('https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/i1hsfwx8_SALAR_Government_Capability_Statement.pdf', 'Capability Statement')}>
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-between">
                    <span className="flex items-center gap-3">
                      <FileText className="text-cyan-400" size={32} />
                      Government Capability Statement
                    </span>
                    <Download className="text-cyan-400" size={24} />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-blue-100 mb-4">
                    Comprehensive overview of SALAR's technical capabilities and engineering focus areas for government and defense applications.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-cyan-300">
                    <FileText size={16} />
                    <span>PDF • 156.7 KB</span>
                  </div>
                </CardContent>
              </Card>

              {/* Quad Chart */}
              <Card className="bg-blue-900/30 border-blue-400/30 hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300 cursor-pointer" onClick={() => handleDownload('https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/ligr72eb_SALAR_TECH_Quad_Chart_Website.pdf', 'Quad Chart')}>
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-between">
                    <span className="flex items-center gap-3">
                      <FileText className="text-blue-400" size={32} />
                      SALAR Technologies Quad Chart
                    </span>
                    <Download className="text-blue-400" size={24} />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-blue-100 mb-4">
                    Visual overview of SALAR's core capabilities, technical focus areas, and value proposition for defense applications.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-blue-300">
                    <FileText size={16} />
                    <span>PDF</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-blue-900/20 backdrop-blur-sm">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-blue-100 leading-relaxed">
              SALAR's approach emphasizes explainability, engineering discipline, public-safe technical framing, 
              and resilience logic rather than black-box claims.
            </p>
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-16 bg-transparent">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto space-y-12">
            {capabilities.map((capability, index) => (
              <Card
                key={capability.id}
                className={
                  capability.flagship
                    ? "border-cyan-400/50 ring-1 ring-cyan-400/30 bg-gradient-to-br from-blue-900/40 to-indigo-950/30 hover:shadow-xl transition-shadow"
                    : "border-blue-500/30 hover:shadow-xl transition-shadow"
                }
                data-testid={capability.flagship ? "capability-flagship-thermal" : undefined}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={
                        capability.flagship
                          ? "w-16 h-16 bg-cyan-500/15 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0 border border-cyan-400/40"
                          : "w-16 h-16 bg-blue-950/30 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0"
                      }>
                        <span className="text-white text-2xl font-bold">{index + 1}</span>
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          {capability.flagship && (
                            <Badge variant="outline" className="border-cyan-400 text-cyan-200 font-mono uppercase tracking-wider text-[10px]">
                              Flagship · AWS Marketplace
                            </Badge>
                          )}
                          {capability.additional && (
                            <Badge variant="outline" className="border-blue-400/60 text-blue-200 font-mono uppercase tracking-wider text-[10px]">
                              Additional Assurance Service
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-2xl text-white">{capability.title}</CardTitle>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base text-blue-100 leading-relaxed">
                    {capability.description}
                  </CardDescription>

                  {capability.flagship && (
                    <div className="mt-5 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                      <a
                        href={capability.awsMarketplaceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="capability-thermal-aws-cta"
                      >
                        <Button className="bg-cyan-500 hover:bg-cyan-400 text-[#0a1628] font-bold">
                          View AWS Marketplace Listing
                          <ArrowRight className="ml-2" size={16} />
                        </Button>
                      </a>
                      <a
                        href={capability.techBriefUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="capability-thermal-techbrief-cta"
                      >
                        <Button variant="outline" className="border-cyan-400 text-cyan-100 hover:bg-cyan-400 hover:text-blue-900 font-semibold">
                          <Download className="mr-2" size={16} />
                          Scenario Brief (PDF)
                        </Button>
                      </a>
                      <Link
                        to="/contact?topic=AWS%20Marketplace%20%2F%20Thermal%20Resilience&subject=Thermal%20Resilience%20Assurance%20Inquiry&message=I%27d%20like%20to%20discuss%20a%20scoped%20engagement%20for%20the%20Data%20Center%20Thermal%20Resilience%20Assurance%20service."
                      >
                        <Button variant="outline" className="border-blue-400/60 text-blue-100 hover:bg-blue-400/20 font-semibold">
                          Scope an Engagement
                        </Button>
                      </Link>
                    </div>
                  )}

                  {capability.additional && (
                    <div className="mt-5">
                      <Link
                        to="/contact?topic=AWS%20Marketplace%20%2F%20Thermal%20Resilience&subject=Assurance%20Service%20Inquiry&message=I%27d%20like%20to%20discuss%20a%20scoped%20engagement%20under%20SALAR%27s%20Additional%20Assurance%20Services."
                      >
                        <Button variant="outline" className="border-cyan-400/60 text-cyan-100 hover:bg-cyan-400 hover:text-blue-900 font-semibold">
                          Scope an Engagement
                          <ArrowRight className="ml-2" size={16} />
                        </Button>
                      </Link>
                    </div>
                  )}

                  {capability.flagship && capability.disclaimer && (
                    <p className="mt-4 text-xs text-blue-300/70 italic">{capability.disclaimer}</p>
                  )}

                  {/* Why It Matters & Approach blocks — index-keyed; only for the original 5 reliability lanes */}
                  {!capability.flagship && !capability.additional && (
                    <>
                      <div className="mt-6 p-4 bg-transparent rounded-lg border border-slate-200">
                        <h4 className="font-semibold text-white mb-2">Why It Matters</h4>
                        <p className="text-sm text-blue-200">
                          {index === 1 && "Board-level reliability issues cascade into field failures, qualification delays, and mission-critical operational risk. Structured design review logic helps identify and mitigate these vulnerabilities early."}
                          {index === 2 && "Understanding failure patterns before they occur enables proactive maintenance, reduces operational downtime, and improves system readiness for defense and strategic applications."}
                          {index === 3 && "Lower-tier supply-chain dependencies remain invisible to most organizations. Graph-based visibility reveals concentration risks, documentation gaps, and pedigree-risk exposure that threaten mission success."}
                          {index === 4 && "Manufacturing drift and process instability lead to quality degradation over time. Closed-loop monitoring and control preserve production consistency and reduce defect risk."}
                          {index === 5 && "Trusted process control and lithography assurance are critical for strategic semiconductor production. Public-safe positioning enables collaboration without exposing sensitive technical details."}
                          {index === 6 && "Post-quantum verifiability protects long-lived signed records — provenance, attestations, and qualification evidence — from becoming worthless when classical cryptography is retired. Critical for air-gapped, defense, and long-lived archive environments."}
                        </p>
                      </div>

                      <div className="mt-6 p-4 bg-blue-950/30 backdrop-blur-sm text-white rounded-lg">
                        <h4 className="font-semibold mb-2">SALAR's Approach</h4>
                        <p className="text-sm text-slate-300">
                          {index === 1 && "Engineering-centered checklist frameworks that integrate design discipline, component selection, thermal management, and qualification requirements into a unified reliability assessment."}
                          {index === 2 && "Explainable analytics that combine historical failure data, operational context, and engineering physics to generate actionable predictions without opaque AI claims."}
                          {index === 3 && "Graph-oriented supply-chain intelligence that maps multi-tier dependencies, scores provenance confidence, identifies documentation gaps, and routes source-path risks into the appropriate human review — not automatic verdicts."}
                          {index === 4 && "Multi-domain stability frameworks that integrate process monitoring, quality signals, and feedback control to detect and correct manufacturing drift before it becomes critical."}
                          {index === 5 && "High-level technical positioning that demonstrates capability and credibility while maintaining appropriate discretion for sensitive process control and lithography technologies."}
                          {index === 6 && "Hardware-anchored bridging from existing TPM / secure-element / HSM roots of trust into portable post-quantum-verifiable receipts — so provenance signed today remains verifiable for decades across the PQ transition."}
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Provenance Confidence & Source-Risk Intelligence */}
      <section className="py-16 bg-blue-950/30 backdrop-blur-sm border-y border-blue-800/30" data-testid="provenance-section">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <Badge variant="outline" className="border-cyan-400 text-cyan-200 mb-4">
                Evidence Governance · Not Automated Verdicts
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Provenance Confidence &amp; Source-Risk Intelligence
              </h2>
              <p className="text-lg text-cyan-200 max-w-3xl mx-auto leading-relaxed">
                Evidence-based decision support for sourcing, qualification, sustainment, and grey-market risk review.
              </p>
            </div>

            <div className="space-y-5 text-blue-100 leading-relaxed max-w-4xl mx-auto">
              <p>
                SALAR Technologies does not make casual counterfeit determinations. Counterfeit avoidance is a nuanced
                evidence problem, not a binary software label. SALAR&apos;s source-confidence layer is designed to help
                engineering, sourcing, quality, and sustainment teams evaluate the accumulated evidence around a
                component or supplier path: source type, chain-of-custody continuity, manufacturer traceability, lot /
                date-code consistency, documentation quality, storage and handling records, inspection status, prior
                anomaly history, and application criticality. The output is an <strong className="text-white">explainable
                decision memo</strong> that separates documentation gaps, source-path risk, pedigree inconsistency,
                inspection anomalies, test-evidence concerns, and recommended next actions.
              </p>
              <p>
                The strongest use case is for teams that do not routinely buy through grey-market or non-authorized
                channels but may face constrained sourcing because of <strong className="text-white">obsolescence,
                allocation, urgent sustainment needs, or long lead times</strong>. SALAR helps these teams recognize when
                multiple weak signals add up to a higher-risk sourcing path — and when additional review, testing,
                quarantine, or engineering escalation is appropriate.
              </p>
            </div>

            {/* 5-Step Workflow */}
            <div className="mt-12" data-testid="provenance-workflow">
              <h3 className="text-xl font-bold text-white mb-6 text-center">Workflow</h3>
              <div className="space-y-3">
                {[
                  'Ingest BOM, supplier, source, lot, logistics, documentation, and inspection evidence.',
                  'Score provenance confidence and source-path risk.',
                  'Separate documentation gaps from inspection anomalies and validated test concerns.',
                  'Route to accept, conditional accept, hold, quarantine, additional testing, engineering review, or reject under customer policy.',
                  'Generate an auditable decision memo showing what evidence drove the recommendation.'
                ].map((step, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-4 p-4 bg-blue-900/30 border border-blue-500/30 rounded-lg"
                    data-testid={`provenance-step-${idx + 1}`}
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-cyan-600 to-blue-700 border-2 border-cyan-300/40 flex items-center justify-center text-white font-bold">
                      {idx + 1}
                    </div>
                    <p className="text-blue-100 leading-relaxed pt-1.5">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* One-line Provenance Confidence card */}
            <div className="mt-8 p-5 bg-blue-900/40 border border-cyan-400/30 rounded-lg" data-testid="provenance-confidence-card">
              <p className="text-blue-100 leading-relaxed">
                <span className="text-cyan-300 font-semibold">Provenance Confidence: </span>
                SALAR helps teams identify accumulated source-risk indicators before release decisions are made, without
                replacing lab testing or expert counterfeit determinations.
              </p>
            </div>

            {/* Technical disclaimer */}
            <div className="mt-6 p-6 bg-amber-950/20 border-l-4 border-amber-400 rounded" data-testid="provenance-disclaimer">
              <p className="text-sm uppercase tracking-widest text-amber-300 font-semibold mb-3">
                Technical Note
              </p>
              <p className="text-blue-100 leading-relaxed">
                SALAR does not replace authorized-channel sourcing, AS6081-style inspection workflows, laboratory
                counterfeit testing, X-ray, decapsulation, electrical or material analysis, or expert quality judgment.
                SALAR complements those workflows by organizing evidence, scoring provenance confidence, identifying
                source-risk patterns, and preserving decision traceability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning Note */}
      <section className="py-16 bg-blue-900/20 backdrop-blur-sm">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="border-[#1e3a5f]">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Capability Positioning Note</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-blue-100 leading-relaxed">
                  SALAR's approach emphasizes explainability, engineering discipline, public-safe technical framing, 
                  and resilience logic rather than black-box claims. Each capability is built on defensible engineering 
                  principles, evidence-based analysis, and transparent decision-making processes suitable for 
                  defense, government, and strategic-industry applications.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-16 text-white"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.85), rgba(10,22,40,0.95)), url('/img/data_center_servers.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="capabilities-cta"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Interested in These Capabilities?</h2>
            <p className="text-lg text-slate-300 mb-8">
              Contact SALAR to discuss technical collaboration, partnership opportunities, or government applications.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-blue-900/20 backdrop-blur-sm text-white hover:bg-slate-100">
                  Start a Conversation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/documents">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-900/20 backdrop-blur-sm/10">
                  Download Capability Statement
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Capabilities;
